import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ScrollToTop } from "./components/ScrollToTop";
import { usePageTracking } from "./hooks/usePageTracking";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import ProjectsList from "./pages/ProjectsList";
import ProjectDetails from "./pages/ProjectDetails";
import NewRunWizard from "./pages/NewRunWizard";
import RunProgress from "./pages/RunProgress";
import RunResults from "./pages/RunResults";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ComingSoon from "./pages/ComingSoon";
import Docs from "./pages/Docs";
import About from "./pages/About";
import Security from "./pages/Security";
import DPA from "./pages/DPA";
import ResetPassword from "./pages/ResetPassword";
import ContactSales from "./pages/ContactSales";
import PersonaLibrary from "./pages/PersonaLibrary";
import OrganizationSettings from "./pages/OrganizationSettings";
import AdminDashboard from "./pages/AdminDashboard";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogEditor from "./pages/BlogEditor";
import BackendDebug from "./pages/BackendDebug";
import DebugExecution from "./pages/DebugExecution";
import DebugIndex from "./pages/DebugIndex";
import { AppLayout } from "./components/layout/AppLayout";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 min global stale time for caching
      gcTime: 10 * 60 * 1000, // 10 min garbage collection
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Route that redirects authenticated users
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Redirect logged-in users from landing to dashboard
function AuthAwareLanding() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
}

function AppRoutes() {
  usePageTracking();
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<AuthAwareLanding />} />
      <Route path="/home" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/about" element={<About />} />
      <Route path="/security" element={<Security />} />
      <Route path="/dpa" element={<DPA />} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/contact-sales" element={<ContactSales />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/new" element={<BlogEditor />} />
      <Route path="/blog/:slug/edit" element={<BlogEditor />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route
        path="/auth"
        element={
          <AuthRoute>
            <Auth />
          </AuthRoute>
        }
      />

      {/* App routes (authenticated) */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/projects" element={<ProjectsList />} />
        <Route path="/dashboard/projects/:projectId" element={<ProjectDetails />} />
        <Route path="/dashboard/new-run" element={<NewRunWizard />} />
        <Route path="/dashboard/runs/:runId" element={<RunProgress />} />
        <Route path="/dashboard/runs/:runId/results" element={<RunResults />} />
        <Route path="/personas" element={<PersonaLibrary />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/organization" element={<OrganizationSettings />} />
      </Route>

      {/* Admin route - unlisted, direct access only */}
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Debug tools - admin only */}
      <Route path="/debug" element={<DebugIndex />} />
      <Route path="/debug/backend" element={<BackendDebug />} />
      <Route path="/debug/execution" element={<DebugExecution />} />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CookieConsent />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <SubscriptionProvider>
            <AppRoutes />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
